import React, { useState, useRef, useCallback, useEffect } from "react";
import { DraftinkImperativeAPI } from "@draftink/draftink/types";
import {
  convertToDraftinkElements,
  TTDDialog,
  exportToCanvas,
  DEFAULT_EXPORT_PADDING,
  canvasToBlob,
} from "@draftink/draftink";
import { useApp } from "@draftink/draftink/components/App";
import { MagicIcon } from "@draftink/draftink/components/icons";
import { useI18n } from "@draftink/draftink/i18n";

// Professional animated chat bubble icon for AI Assistant
const AIAssistantIcon = (
  <svg className="ai-assistant-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122 122" width="50" height="50" fill="none" strokeLinecap="round" strokeLinejoin="round" preserveAspectRatio="xMidYMid meet">
    {/* Gradient definition with professional blue tones */}
    <defs>
      <linearGradient id="chatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#30967c" />
        <stop offset="100%" stopColor="#237862" />
      </linearGradient>
      
      {/* Refined wave filter with controlled amplitude for subtle animation */}
      <filter id="subtleWave" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="turbulence" baseFrequency="0.03" numOctaves="2" seed="0" stitchTiles="stitch" result="turbulence">
          {/* Smoother animation */}
          <animate attributeName="seed" from="1" to="100" dur="20s" repeatCount="indefinite" />
        </feTurbulence>
        {/* Reduced scale for elegant, minimal distortion */}
        <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="6" />
      </filter>
    </defs>
    
    {/* Circular chat bubble with tail, refined path for cleaner shape */}
    <path 
      d="M111 61c0 27.6-22.4 50-50 50c-10 0-19.3-3-27-8l-23 17V61c0-27.6 22.4-50 50-50s50 22.4 50 50z" 
      fill="none" 
      stroke="url(#chatGradient)" 
      strokeWidth="3"
      filter="url(#subtleWave)"
    />
    
    {/* Letter T centered, with adjusted proportions */}
    <path 
      d="M39 48h46M62 48v34" 
      stroke="url(#chatGradient)" 
      strokeWidth="5"
      strokeLinecap="round" 
      strokeLinejoin="round" 
      filter="url(#subtleWave)" 
    />
  </svg>
);
import { 
  analyzeCanvas, 
  buildContextString, 
  calculateSmartPosition,
  getElementsBounds,
  validateGeneratedElements,
  CanvasSize,
  resolveElementTargets  // NEW: Helper to resolve "target" descriptions to IDs
} from "./AIAssistantContext";
import "./AIAssistant.scss";

// Environment configuration
const ENV = 'prod'; // 'dev' for development, 'prod' for production

const CONFIG = {
  dev: {
    apiUrl: 'http://localhost:3006/api/ai/chat',
    apiKey: '', // No API key needed - proxy handles it
    model: 'codex-1a',
    debugMode: true,
  },
  prod: {
    apiUrl: '/api/ai/chat', // Will be proxied through Nginx
    apiKey: '', // No API key needed - proxy handles it
    model: 'codex-1a',
    debugMode: false,
  }
};

const currentConfig = CONFIG[ENV as keyof typeof CONFIG];

// Debug logger that respects environment
const debugLog = (...args: any[]) => {
  if (currentConfig.debugMode) {
    console.log('[AI Assistant]', ...args);
  }
};

interface AIAssistantProps {
  draftinkAPI: DraftinkImperativeAPI;
}

// Interface for AI response
interface AIAction {
  action: "generate_mermaid" | "modify_elements" | "clear_canvas" | "error";
  params: any;
}

interface ModifyOperation {
  op: "update" | "delete" | "connect" | "move";
  target: string;
  details?: any;
}

export const AIAssistant: React.FC<AIAssistantProps> = ({ draftinkAPI }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPointer, setShowPointer] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useI18n();

  // Localized strings
  const assistantTitle = t('aiAssistant.title');
  const placeholderText = t('aiAssistant.placeholder');

  // Debug effect to monitor state
  useEffect(() => {
    debugLog("State changed:", { isLoading, isExpanded, hasError: !!error });
  }, [isLoading, isExpanded, error]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [input]);

  useEffect(() => {
    // Hide pointer after first interaction
    if (isExpanded) {
      setShowPointer(false);
    }
  }, [isExpanded]);

  // Periodically show pointer to remind users about AI Assistant
  useEffect(() => {
    const showPointerPeriodically = () => {
      // Only show if not expanded and not already showing
      if (!isExpanded && !showPointer) {
        setShowPointer(true);
        
        // Hide after 5 seconds
        const hideTimeout = setTimeout(() => {
          setShowPointer(false);
        }, 5000);
        
        return () => clearTimeout(hideTimeout);
      }
    };
    
    // Show pointer every 2 minutes
    const interval = setInterval(showPointerPeriodically, 120000);
    
    // Also show on first load after 30 seconds
    const initialTimeout = setTimeout(() => {
      showPointerPeriodically();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
  }, [isExpanded, showPointer]);

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isLoading) {
      debugLog("Prevented submission:", { input: input.trim(), isLoading });
      return;
    }

    debugLog("Starting submission:", { input, isLoading });
    setIsLoading(true);
    setError(null);

    let attempts = 0;
    const maxAttempts = 2;  // For retry on syntax errors

    try {
      while (attempts < maxAttempts) {
        let timeoutId: NodeJS.Timeout | undefined;
        
        try {
          debugLog(`Attempt ${attempts + 1}/${maxAttempts}`);
          const elements = draftinkAPI.getSceneElements();
          const canvasContext = analyzeCanvas(elements);
          const contextString = buildContextString(canvasContext);

          debugLog("Canvas analysis:", { 
            elementCount: elements.length,
            canvasSize: canvasContext.size,
            contextString 
          });

          if (canvasContext.size >= CanvasSize.LARGE) {
            setError("Canvas is large. AI may suggest focused changes.");
          }

          // AI call with structured output
          debugLog("Making AI API call...");
          
          // Add timeout to prevent hanging
          const controller = new AbortController();
          timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
          
          try {
            const response = await fetch(currentConfig.apiUrl, {
              method: "POST",
              headers: {
                // No Authorization header needed - proxy handles it
                "Content-Type": "application/json",
              },
              signal: controller.signal,
              body: JSON.stringify({
                model: currentConfig.model,
                // Send only context and user input - prompt handled server-side
                context: contextString,
                userInput: input,
                // Keep messages format for backward compatibility
                messages: [
                  {
                    role: "system",
                    content: `You are a smart diagram assistant for an Excalidraw-like tool. Based on the user request and canvas context, decide the best action and output ONLY JSON (no explanations).

Possible actions (choose one):
- "generate_mermaid": Generate new Mermaid code. Params: { "code": "clean Mermaid code string" }
- "modify_elements": Modify existing elements (e.g., update label, move, connect). Params: { "operations": array of { "op": "update"|"delete"|"connect"|"move", "target": "element ID or description", "details": object } }
- "clear_canvas": Clear all elements. Params: {} (empty)
- "error": If unclear or impossible. Params: { "message": "user-friendly error" }

Rules:
- For new diagrams or nodes: Use "generate_mermaid". If the request says "add", "create", or "new", generate Mermaid code for the new elements and integrate it with the existing diagram if appropriate.
- For modifications: Use if request says "change", "update", "delete", "connect". Describe targets naturally (code will resolve IDs).
- When connecting elements: If a target node in a 'connect' request doesn't exist on the canvas, use the 'generate_mermaid' action to add the missing node AND the new connection. For example, if 'A' exists and user says 'connect A to B', generate Mermaid code that adds 'A --> B'.
- For edges like "clear all": Use "clear_canvas".
- Fix syntax: Ensure valid Mermaid (quote specials, no unsupported shapes).
- Keep small: <100 new elements.
- Context: ${contextString}

IMPORTANT for error handling:
- If user says "delete" without specifying what, respond with error: "Please specify which element to delete. For example: 'delete the Start node' or 'delete all rectangles'"
- If request is too vague, ask for clarification in the error message
- If no elements exist but user wants to modify, respond with error: "No elements on canvas to modify. Try creating a diagram first."
- Be helpful in error messages - suggest what the user could say instead

IMPORTANT Mermaid syntax rules:
- Always wrap node text containing special characters in double quotes
- Special characters include: ( ) [ ] { } + - = > < | & % # @ ! ? , . ; : ' " / \\ AND non-ASCII characters (Cyrillic, Chinese, etc.)
- Example: A["Text with (parentheses) + symbols"]
- For non-ASCII text, always use quotes: A["Текст на русском"]
- Avoid complex quotes and escaping - use simple single quotes (') instead of double quotes (") inside text

SUPPORTED NODE SHAPES in flowcharts:
- Rectangle: Node["Text"] - USE THIS FOR MOST NODES
- Circle: Node((Text))
- Diamond: Node{Text} - for decisions/conditions
- DO NOT USE: [/text/], [[text]], >text], {{text}}, [/text\\] - these are NOT supported

SUPPORTED DIAGRAM TYPES:
- Flowcharts: flowchart TD or flowchart LR (PREFERRED - use this for most requests)
- Sequence diagrams: sequenceDiagram  
- Class diagrams: classDiagram
- Gantt charts: gantt
- Pie charts: pie
- XY charts: xychart-beta (for bar/line charts)

XYCHART-BETA SYNTAX RULES:
- Always use quoted strings for titles and labels
- Avoid special characters in axis labels - use simple text or transliterate
- MUST include both bar and line elements for proper rendering
- DO NOT include any styling or classDef - xychart-beta doesn't support it
- Format:
  xychart-beta
    title "Chart Title"
    x-axis [label1, label2, label3]
    y-axis "Y Axis Label" min --> max
    bar [value1, value2, value3]
    line [value1, value2, value3]

PROFESSIONAL STYLING FOR FLOWCHARTS:
- Add subtle colors to make diagrams more visually appealing
- IMPORTANT: Styling with classDef is ONLY for flowcharts, NOT for other diagram types
- Use these professional color schemes:
  %% Styling
  classDef default fill:#f9f9f9,stroke:#333,stroke-width:2px
  classDef primary fill:#e1f5fe,stroke:#0288d1,stroke-width:2px,color:#01579b
  classDef success fill:#e8f5e9,stroke:#388e3c,stroke-width:2px,color:#1b5e20
  classDef warning fill:#fff3e0,stroke:#f57c00,stroke-width:2px,color:#e65100
  classDef danger fill:#ffebee,stroke:#d32f2f,stroke-width:2px,color:#b71c1c
  classDef info fill:#f3e5f5,stroke:#7b1fa2,stroke-width:2px,color:#4a148c
  
- Apply styles to nodes using: class NodeName className

PREFER SIMPLE DIAGRAMS:
- For complex data visualization, consider if a flowchart would be clearer
- For simple bar/line charts, xychart-beta is appropriate
- Keep it simple - flowcharts are the most reliable for complex relationships

Examples:
User: Create a simple flowchart.
Output: { "action": "generate_mermaid", "params": { "code": "flowchart TD\\n    A[Start] --> B[Process]\\n    B --> C{Decision}\\n    C -->|Yes| D[Success]\\n    C -->|No| E[Retry]\\n    E --> B\\n    D --> F[End]\\n    \\n    %% Styling\\n    classDef startEnd fill:#e8f5e9,stroke:#388e3c,stroke-width:2px,color:#1b5e20\\n    classDef decision fill:#fff3e0,stroke:#f57c00,stroke-width:2px,color:#e65100\\n    classDef process fill:#e1f5fe,stroke:#0288d1,stroke-width:2px,color:#01579b\\n    \\n    class A,F startEnd\\n    class C decision\\n    class B,E process\\n    class D startEnd" } }

User: Make a pie chart of pets.
Output: { "action": "generate_mermaid", "params": { "code": "pie title \\"Pets adopted by volunteers\\"\\n    \\"Dogs\\" : 386\\n    \\"Cats\\" : 85\\n    \\"Rats\\" : 15" } }

User: Show revenue forecast.
Output: { "action": "generate_mermaid", "params": { "code": "xychart-beta\\n    title \\"Revenue Forecast\\"\\n    x-axis [2025, 2026, 2027]\\n    y-axis \\"Billions\\" 0 --> 10\\n    bar [4, 7, 10]\\n    line [4, 7, 10]" } }

User: Create a chart for PAO ИВА revenue.
Output: { "action": "generate_mermaid", "params": { "code": "xychart-beta\\n    title \\"IVA Revenue\\"\\n    x-axis [2025, 2026, 2027]\\n    y-axis \\"Billion RUB\\" 0 --> 10\\n    bar [5, 7, 9]\\n    line [5, 7, 9]" } }

User: Create a sales chart.
Output: { "action": "generate_mermaid", "params": { "code": "xychart-beta\\n    title \\"Sales Revenue\\"\\n    x-axis [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec]\\n    y-axis \\"Revenue (in $)\\" 4000 --> 11000\\n    bar [5000, 6000, 7500, 8200, 9500, 10500, 11000, 10200, 9200, 8500, 7000, 6000]\\n    line [5000, 6000, 7500, 8200, 9500, 10500, 11000, 10200, 9200, 8500, 7000, 6000]" } }

User: Create revenue chart with yearly data.
Output: { "action": "generate_mermaid", "params": { "code": "xychart-beta\\n    title \\"Yearly Revenue\\"\\n    x-axis [2020, 2021, 2022, 2023, 2024]\\n    y-axis \\"Revenue (M$)\\" 0 --> 100\\n    bar [20, 35, 50, 75, 95]\\n    line [20, 35, 50, 75, 95]" } }

User: Delete the node labeled 'Start'.
Output: { "action": "modify_elements", "params": { "operations": [{ "op": "delete", "target": "node labeled 'Start'", "details": {} }] } }

User: Clear all.
Output: { "action": "clear_canvas", "params": {} }

User: Add arrow from A to B.
Output: { "action": "modify_elements", "params": { "operations": [{ "op": "connect", "target": "from A to B", "details": { "arrow_type": "straight" } }] } }

User: Invalid request.
Output: { "action": "error", "params": { "message": "Please clarify your request." } }`
                  },
                  {
                    role: "user",
                    content: `User request: ${input}\nCanvas context: ${contextString}`,
                  },
                ],
                temperature: 0.1,
                max_tokens: 1000,
                stream: false,
              }),
            });

            debugLog("API response status:", response.status);
            if (!response.ok) throw new Error(`API error: ${response.status}`);

            const data = await response.json();
            const aiOutput = data.choices[0]?.message?.content?.trim();
            debugLog("AI raw output:", aiOutput);
            
            let parsedAction: AIAction;
            try {
              parsedAction = JSON.parse(aiOutput);
              debugLog("Parsed action:", parsedAction);
            } catch (err) {
              console.error("[AI Assistant] Failed to parse AI output as JSON:", err);
              throw new Error("AI response was not valid JSON");
            }

            if (!parsedAction || !parsedAction.action) throw new Error("Invalid AI output");

            // Branch based on AI decision
            let updatedElements = [...elements];

            switch (parsedAction.action) {
              case "generate_mermaid": {
                debugLog("Generating Mermaid diagram...");
                let mermaidCode = parsedAction.params.code;
                
                // Clean up the mermaid code
                mermaidCode = mermaidCode
                  .replace(/^```mermaid\s*/i, "")
                  .replace(/^```\s*/i, "")
                  .replace(/\s*```$/i, "")
                  .trim();

                // Fix common Mermaid syntax issues
                // Fix malformed parallelogram/special shape syntax
                mermaidCode = mermaidCode.replace(
                  /(\w+)\[\/(.*?)\/\]/g,
                  (match, nodeId, nodeText) => {
                    const cleanText = nodeText.replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
                    return `${nodeId}["${cleanText.replace(/"/g, "'")}"]`;
                  }
                );
                
                // Fix other special shapes that aren't supported
                mermaidCode = mermaidCode.replace(
                  /(\w+)\[\[(.*?)\]\]/g,
                  (match, nodeId, nodeText) => {
                    const cleanText = nodeText.replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
                    return `${nodeId}["${cleanText.replace(/"/g, "'")}"]`;
                  }
                );
                
                // Fix unquoted node text with special characters in flowcharts
                mermaidCode = mermaidCode.replace(
                  /(\w+)\[([^\]\/\[]+)\]/g,
                  (match, nodeId, nodeText) => {
                    if (nodeText.trim().startsWith('"') && nodeText.trim().endsWith('"')) {
                      return match;
                    }
                    const needsQuotes = /[()[\]{}+\-=><|&%#@!?,.;:'"\/\\]/.test(nodeText);
                    if (needsQuotes) {
                      const cleanText = nodeText.replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
                      return `${nodeId}["${cleanText.replace(/"/g, "'")}"]`;
                    }
                    return match;
                  }
                );

                debugLog("Cleaned Mermaid code:", mermaidCode);
                const mermaidApi = await import("@draftink/mermaid-to-draftink");
                let parsedResult;
                try {
                  parsedResult = await mermaidApi.parseMermaidToDraftInk(mermaidCode);
                  debugLog("Mermaid parsed successfully");
                } catch (err) {
                  console.error("[AI Assistant] Mermaid parse error:", err);
                  if (attempts < maxAttempts - 1) {
                    attempts++;
                    continue;  // Retry AI call
                  }
                  throw new Error("Failed to parse generated diagram. Please try a simpler request.");
                }

                const { elements: mermaidElements, files } = parsedResult;
                const excalidrawElements = convertToDraftinkElements(mermaidElements, { regenerateIds: true });

                const validation = validateGeneratedElements(excalidrawElements, updatedElements);
                if (!validation.valid) throw new Error(validation.error);

                const existingBounds = getElementsBounds(updatedElements);
                const newBounds = getElementsBounds(excalidrawElements);
                const position = calculateSmartPosition(newBounds, existingBounds, input);

                const positionedElements = excalidrawElements.map(el => ({
                  ...el,
                  x: el.x + position.x,
                  y: el.y + position.y
                }));

                updatedElements = [...updatedElements, ...positionedElements];
                
                if (files) {
                  draftinkAPI.addFiles(Object.values(files));
                }
                debugLog("Added", positionedElements.length, "new elements");
                break;
              }

              case "modify_elements": {
                debugLog("Modifying elements...");
                const operations = parsedAction.params.operations as ModifyOperation[];
                let modifiedCount = 0;
                
                for (const op of operations) {
                  const targetIds = resolveElementTargets(elements, op.target);
                  debugLog(`Operation ${op.op} on "${op.target}" resolved to IDs:`, targetIds);
                  
                  if (targetIds.length === 0 && op.op !== "connect") {
                    setError(`No elements found matching "${op.target}". Please check the element name and try again.`);
                    return; // Exit gracefully
                  }

                  switch (op.op) {
                    case "delete":
                      const beforeCount = updatedElements.length;
                      // Also delete bound text elements
                      const idsToDelete = new Set(targetIds);
                      targetIds.forEach(id => {
                        // Find any text elements bound to this shape
                        const boundTextElements = updatedElements.filter(el => 
                          el.type === 'text' && 
                          'containerId' in el && 
                          el.containerId === id
                        );
                        boundTextElements.forEach(textEl => idsToDelete.add(textEl.id));
                      });
                      
                      updatedElements = updatedElements.filter(el => !idsToDelete.has(el.id));
                      const deletedCount = beforeCount - updatedElements.length;
                      debugLog("Deleted", deletedCount, "elements (including bound text)");
                      modifiedCount += deletedCount;
                      break;
                    case "update":
                      // Handle text updates specially
                      if (op.details?.new_label || op.details?.text) {
                        const newText = op.details.new_label || op.details.text;
                        
                        targetIds.forEach(targetId => {
                          const element = updatedElements.find(el => el.id === targetId);
                          if (element) {
                            // If it's a text element, update its text
                            if (element.type === 'text') {
                              updatedElements = updatedElements.map(el => 
                                el.id === targetId ? { ...el, text: newText } : el
                              );
                            } 
                            // If it's a shape, find and update its bound text
                            else {
                              const boundText = updatedElements.find(el => 
                                el.type === 'text' && 
                                'containerId' in el && 
                                el.containerId === targetId
                              );
                              if (boundText) {
                                updatedElements = updatedElements.map(el => 
                                  el.id === boundText.id ? { ...el, text: newText } : el
                                );
                              }
                            }
                          }
                        });
                      } else {
                        // Regular property update
                        updatedElements = updatedElements.map(el => 
                          targetIds.includes(el.id) ? { ...el, ...op.details } : el
                        );
                      }
                      debugLog("Updated", targetIds.length, "elements");
                      modifiedCount += targetIds.length;
                      break;
                    case "connect":
                      // Simple arrow creation between elements
                      if (targetIds.length >= 2) {
                        const sourceEl = updatedElements.find(el => el.id === targetIds[0]);
                        const targetEl = updatedElements.find(el => el.id === targetIds[1]);
                        
                        if (sourceEl && targetEl) {
                          const sourceCenter = {
                            x: sourceEl.x + (sourceEl.width || 100) / 2,
                            y: sourceEl.y + (sourceEl.height || 100) / 2
                          };
                          const targetCenter = {
                            x: targetEl.x + (targetEl.width || 100) / 2,
                            y: targetEl.y + (targetEl.height || 100) / 2
                          };
                          
                          const arrow = {
                            id: `arrow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            type: "arrow" as const,
                            x: sourceCenter.x,
                            y: sourceCenter.y,
                            width: targetCenter.x - sourceCenter.x,
                            height: targetCenter.y - sourceCenter.y,
                            angle: 0,
                            strokeColor: "#1e1e1e",
                            backgroundColor: "transparent",
                            fillStyle: "solid" as const,
                            strokeWidth: 2,
                            strokeStyle: "solid" as const,
                            roughness: 1,
                            opacity: 100,
                            startBinding: { 
                              elementId: targetIds[0],
                              focus: 0,
                              gap: 0
                            },
                            endBinding: { 
                              elementId: targetIds[1],
                              focus: 0,
                              gap: 0
                            },
                            points: [[0, 0], [targetCenter.x - sourceCenter.x, targetCenter.y - sourceCenter.y]],
                            lastCommittedPoint: null,
                            startArrowhead: null,
                            endArrowhead: "arrow" as const,
                            locked: false,
                            isDeleted: false,
                          };
                          updatedElements.push(arrow);
                          debugLog("Created arrow connection");
                        }
                      }
                      break;
                    case "move":
                      if (op.details?.x !== undefined && op.details?.y !== undefined) {
                        updatedElements = updatedElements.map(el => 
                          targetIds.includes(el.id) ? { ...el, x: op.details.x, y: op.details.y } : el
                        );
                        debugLog("Moved", targetIds.length, "elements");
                        modifiedCount += targetIds.length;
                      }
                      break;
                    default:
                      throw new Error(`Unknown operation: ${op.op}`);
                  }
                }
                
                // Check if any modifications were made
                if (modifiedCount === 0 && operations.some(op => op.op !== "connect")) {
                  setError("No elements were modified. Please check your request and try again.");
                  return;
                }
                
                break;
              }

              case "clear_canvas": {
                debugLog("Clearing canvas...");
                updatedElements = [];
                break;
              }

              case "error": {
                debugLog("AI reported error:", parsedAction.params.message);
                setError(parsedAction.params.message);
                setIsLoading(false);
                return; // Exit gracefully without throwing
              }

              default:
                throw new Error(`Unknown action: ${parsedAction.action}`);
            }

            // Apply changes
            debugLog("Applying changes to scene...");
            draftinkAPI.updateScene({ elements: updatedElements });

            // Success - reset everything
            setInput("");
            setIsExpanded(false);
            setError(null); // Clear any previous errors
            debugLog("Success! Reset state.");
            // Don't set loading false here - let finally block handle it
            return;  // Success
          } catch (err: any) {
            console.error("[AI Assistant] Error in attempt", attempts + 1, ":", err);
            
            // Retry logic for syntax errors
            if (err.message?.includes("parse") && attempts < maxAttempts - 1) {
              attempts++;
              debugLog("[AI Assistant] Retrying due to parse error...");
              continue;
            }
            
            // If we've exhausted all attempts or it's not a retryable error
            setError(err.message || "Failed to process request.");
            setIsLoading(false);  // Make sure to reset loading state
            debugLog("[AI Assistant] Failed after all attempts. Reset loading state.");
            break;  // Exit the while loop
          } finally {
            if (timeoutId) {
              clearTimeout(timeoutId); // Clear the timeout
            }
          }
        } catch (err: any) {
          // This block was missing. It catches errors from the inner try.
          console.error("[AI Assistant] Inner try-catch error:", err);
          setError(err.message || "An error occurred during the attempt.");
        }
      }
    } catch (outerErr: any) {
      // Catch any unexpected errors at the outer level
      console.error("[AI Assistant] Unexpected error:", outerErr);
      setError(outerErr.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
      debugLog("[AI Assistant] Submission finished. Reset loading state.");
    }
  }, [input, draftinkAPI, isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={`ai-assistant ${isExpanded ? "expanded" : ""} ${showPointer ? "show-pointer" : ""}`}>
      {!isExpanded ? (
        <>
          <button
            className="ai-assistant__trigger"
            onClick={() => setIsExpanded(true)}
            title={`${assistantTitle} - Generate diagrams (${ENV.toUpperCase()} mode)`}
          >
            {AIAssistantIcon}
          </button>
          {showPointer && (
            <div className="ai-assistant__pointer">
              <div className="ai-assistant__pointer-label tdraftinkfont">
                {assistantTitle}
              </div>
              <svg width="38" height="78" viewBox="0 0 38 78" fill="none" className="ai-assistant__pointer-arrow">
                <path
                  d="M1 77c14-2 31.833-11.973 35-24 3.167-12.016-6-35-9.5-43.5"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="m24.165 1.093-2.132 16.309 13.27-4.258-11.138-12.05Z"
                  fill="currentColor"
                />
                <path
                  d="M24.165 1.093c-.522 3.953-1.037 7.916-2.132 16.309m2.131-16.309c-.835 6.424-1.68 12.854-2.13 16.308m0 0c3.51-1.125 7.013-2.243 13.27-4.257m-13.27 4.257c5.038-1.608 10.08-3.232 13.27-4.257m0 0c-3.595-3.892-7.197-7.777-11.14-12.05m11.14 12.05c-3.837-4.148-7.667-8.287-11.14-12.05"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}
        </>
      ) : (
        <div className="ai-assistant__panel">
          <div className="ai-assistant__header">
            <span className="ai-assistant__title">
              {assistantTitle}
              {ENV === 'dev' && (
                <span className="ai-assistant__dev-badge">
                  DEV
                </span>
              )}
            </span>
            <button
              className="ai-assistant__close"
              onClick={() => {
                setIsExpanded(false);
                setError(null);
              }}
            >
              ×
            </button>
          </div>
          <div className="ai-assistant__content">
            <div className="ai-assistant__input-wrapper">
              <textarea
                ref={textareaRef}
                className="ai-assistant__input"
                placeholder={placeholderText}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoFocus
                rows={1}
              />
              <button
                className="ai-assistant__submit"
                onClick={handleSubmit}
                disabled={!input.trim() || isLoading}
              >
                {isLoading ? (
                  <span className="ai-assistant__loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1.5a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.793V2a.5.5 0 0 1 .5-.5z" transform="rotate(180 8 8)"/>
                  </svg>
                )}
              </button>
            </div>
            {error && <div className="ai-assistant__error">{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
};